import { Component, ElementRef, EventEmitter, inject, Input, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TimeEntry, TimelineTask } from '../../../../shared/models/activity.model';
import { TauriService } from '../../../../shared/services/tauri.service';
import { AiService } from '../../../../shared/services/ai.service';
import { ProjectService } from '../../../../shared/services/project.service';
import { TaskService } from '../../../../shared/services/task.service';
import { Project, ProjectStage } from '../../../../shared/models/project.model';
import { Task } from '../../../../shared/models/task.model';
import { UserService } from '../../../../shared/services/user.service';
import { DEFAULT_INVOICE_PROMPT } from '../../../../shared/models/user.model';
import { PosthogService } from '../../../../shared/services/posthog.service';
import { FileService } from '../../../../shared/services/file-s3.service';
import { ClientService, Todo } from '../../../../shared/services/client.service';
import { Subscription } from 'rxjs';

// MODAL TO ENTER TIME ENTRY
@Component({
  selector: 'app-entry-modal',
  imports: [CommonModule, FormsModule],
  templateUrl: './entry-modal.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntryModalComponent implements OnInit, OnDestroy {
  private projectService = inject(ProjectService);
  private taskService = inject(TaskService);
  private tauriService = inject(TauriService)
  private aiService = inject(AiService);
  private userService = inject(UserService);
  private cdr = inject(ChangeDetectorRef);
  posthogService = inject(PosthogService);
  private fileService = inject(FileService);
  private clientService = inject(ClientService);

  @Input({ required: true }) startTime!: Date;
  @Input({ required: true }) endTime!: Date;
  /** 'modal' floats over a backdrop; 'page' fills whatever the route gives it. */
  @Input() presentation: 'modal' | 'page' = 'modal';
  @Output() save = new EventEmitter<TimeEntry>();
  @Output() cancel = new EventEmitter<void>();

  /** In page mode the backdrop is removed from layout entirely (display: contents). */
  get backdropClasses(): string {
    return this.presentation === 'page'
      ? 'contents'
      : 'fixed inset-0 z-9999 flex items-center justify-center bg-black/40 backdrop-blur-sm p-2 entry-modal-backdrop';
  }

  get containerClasses(): string {
    const base = 'bg-white rounded-2xl border border-gray-200 flex flex-col overflow-hidden w-full';
    // `entry-modal-container` only caps max-height for the modal; a page must not inherit it.
    return this.presentation === 'page'
      ? `${base} h-full`
      : `${base} shadow-2xl max-w-4xl max-h-[96vh] entry-modal-container`;
  }

  projects: Project[] = [];
  allStages: ProjectStage[] = [];
  filteredStages: ProjectStage[] = [];
  allTasks: Task[] = [];
  /** All tasks of the selected stage (status-sorted), before the open/all filter. */
  stageTasks: Task[] = [];
  filteredTasks: Task[] = [];

  // Project picker (searchable dropdown, alphabetical)
  @ViewChild('projectSearchInput') projectSearchInput?: ElementRef<HTMLInputElement>;
  projectDropdownOpen = false;
  projectSearch = '';
  filteredProjects: Project[] = [];

  // Task filter — open tasks only by default, switchable to all
  taskStatusFilter: 'open' | 'all' = 'open';
  allTimelineActivity: TimelineTask[] = [];
  selectedActivity: TimelineTask[] = [];

  selectedFiles: File[] = [];
  isUploading = false;

  selectedProjectId: string = '';
  selectedStageId: string = '';
  selectedTaskId: string = '';

  // Phase picker (searchable, mirrors the project picker below)
  @ViewChild('stageSearchInput') stageSearchInput?: ElementRef<HTMLInputElement>;
  stageDropdownOpen = false;
  stageSearch = '';
  /** `filteredStages` narrowed by the search box — this is what the list renders. */
  searchedStages: ProjectStage[] = [];

  // Task picker (searchable, on top of the existing open/all switch)
  @ViewChild('taskSearchInput') taskSearchInput?: ElementRef<HTMLInputElement>;
  taskDropdownOpen = false;
  taskSearch = '';
  /** `filteredTasks` narrowed by the search box — this is what the list renders. */
  searchedTasks: Task[] = [];

  // Quick task creation, so an empty phase is no longer a dead end here
  @ViewChild('newTaskInput') newTaskInput?: ElementRef<HTMLInputElement>;
  creatingTask = false;
  newTaskName = '';
  isSavingTask = false;

  // CRM task mode
  entrySource: 'project' | 'crm' = 'project';
  crmTasks: Todo[] = [];
  filteredCrmTasks: Todo[] = [];
  crmTaskSearch = '';
  selectedCrmTaskId = '';

  billable = true;  // defaults to billable

  projectName = '';
  content = '';
  userPrompt = '';
  statusOptions = [
    { text: 'Not Begin', value: 'NOTBEGIN' },
    { text: 'In Progress', value: 'INPROGRESS' },
    { text: 'Done', value: 'DONE' }
  ];

  // Pré-calculer la durée
  durationText = '';
  isLoading = false; // Désactiver le loading par défaut
  private subscriptions: Subscription[] = [];

  // Custom time picker options
  // Editable local copies of start/end time
  editStartTime = '';
  editEndTime = '';
  editStartHour = 0;
  editStartMinute = 0;
  editEndHour = 0;
  editEndMinute = 0;
  // Working copies as Date (mutated when user changes time)
  editedStartDate!: Date;
  editedEndDate!: Date;

  ngOnInit() {
    // Initialize editable time copies from inputs
    this.editedStartDate = new Date(this.startTime);
    this.editedEndDate = new Date(this.endTime);
    this.editStartHour = this.startTime.getHours();
    this.editStartMinute = this.startTime.getMinutes();
    this.editEndHour = this.endTime.getHours();
    this.editEndMinute = this.endTime.getMinutes();
    this.editStartTime = this.formatTime(this.startTime);
    this.editEndTime = this.formatTime(this.endTime);

    // Pré-calculer la durée immédiatement (pas de service call)
    this.durationText = this.calculateDurationText();

    console.log('🟢 [MODAL] ngOnInit for period:', this.startTime.toISOString(), 'to', this.endTime.toISOString());

    // Utiliser des subscriptions normales (PAS take(1)) pour recevoir les mises à jour
    this.subscriptions.push(
      this.projectService.projects$.subscribe(projects => {
        this.projects = [...projects].sort((a, b) =>
          (a.projectName || '').localeCompare(b.projectName || '', undefined, { sensitivity: 'base' }));
        this.filterProjects();
        console.log('🟢 [MODAL] Received', projects.length, 'projects');
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.push(
      this.projectService.projectsStage$.subscribe(stages => {
        this.allStages = stages;
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.push(
      this.taskService.tasks$.subscribe(tasks => {
        this.allTasks = tasks;
        this.cdr.markForCheck();
      })
    );

    // Subscription la plus importante : timeline tasks
    this.subscriptions.push(
      this.tauriService.timelineTasks$.subscribe(timelineTasks => {
        console.log('🟢 [MODAL] timelineTasks$ emitted with', timelineTasks.length, 'tasks');
        this.allTimelineActivity = timelineTasks;
        this.filterTask();
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.push(
      this.userService.settings$.subscribe(settings => {
        const savedPrompt = settings?.aiInvoicePrompt;
        this.userPrompt = (savedPrompt && savedPrompt.trim().length > 0) ? savedPrompt : DEFAULT_INVOICE_PROMPT;
        this.cdr.markForCheck();
      })
    );
    // Load assigned CRM tasks in parallel
    this.loadCrmTasks();
  }

  ngOnDestroy() {
    // Cleanup toutes les subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    console.log('🟢 [MODAL] Destroyed - Cleaned up subscriptions');
  }

  // ── CRM task helpers ─────────────────────────────────────────────────────

  private async loadCrmTasks() {
    // Use `let` to avoid TDZ crash when BehaviorSubject emits synchronously
    let sub: any;
    const currentUser = await new Promise<any>((resolve) => {
      sub = this.userService.currentUser$.subscribe(u => {
        if (u) { sub?.unsubscribe(); resolve(u); }
      });
    });
    if (!currentUser?.id) {
      console.warn('[EntryModal] No current user — skipping CRM task load');
      return;
    }
    try {
      console.log('[EntryModal] Loading CRM tasks for user:', currentUser.id);
      const ids = await this.clientService.getTodosAssignedToUser(currentUser.id);
      console.log('[EntryModal] Assigned todo IDs:', ids.length);
      if (!ids.length) {
        this.crmTasks = [];
        this.filteredCrmTasks = [];
        this.cdr.markForCheck();
        return;
      }
      const { todos } = await this.clientService.fetchTodosPage({ ids, limit: 100 });
      console.log('[EntryModal] Loaded CRM tasks:', todos.length);
      this.crmTasks = todos.sort((a: Todo, b: Todo) => this.crmStatusOrder(a.status) - this.crmStatusOrder(b.status));
      this.filteredCrmTasks = [...this.crmTasks];
      this.cdr.markForCheck();
    } catch (e) {
      console.error('[EntryModal] Failed to load CRM tasks', e);
    }
  }

  switchSource(src: 'project' | 'crm') {
    this.entrySource = src;
    // Reset selections
    this.selectedProjectId = '';
    this.selectedStageId = '';
    this.selectedTaskId = '';
    this.filteredStages = [];
    this.stageTasks = [];
    this.filteredTasks = [];
    this.taskStatusFilter = 'open';
    this.projectDropdownOpen = false;
    this.projectSearch = '';
    this.filterProjects();
    this.selectedCrmTaskId = '';
    this.crmTaskSearch = '';
    this.filteredCrmTasks = [...this.crmTasks];
    this.cdr.markForCheck();
  }

  onCrmSearch() {
    const q = this.crmTaskSearch.toLowerCase();
    const list = q
      ? this.crmTasks.filter(t => t.name.toLowerCase().includes(q))
      : [...this.crmTasks];
    this.filteredCrmTasks = list.sort((a, b) => this.crmStatusOrder(a.status) - this.crmStatusOrder(b.status));
  }

  private crmStatusOrder(status: string | null | undefined): number {
    switch (status) {
      case 'IN_PROGRESS': return 0;
      case 'TODO': return 1;
      case 'DONE': return 2;
      default: return 1;
    }
  }

  crmTaskLabel(task: Todo): string {
    const status = task.status === 'DONE' ? '✓' : task.status === 'IN_PROGRESS' ? '▶' : '○';
    return `${status} ${task.name}`;
  }

  private roundToNearest5(min: number): number {
    const snapped = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
    return snapped.reduce((prev, curr) =>
      Math.abs(curr - min) < Math.abs(prev - min) ? curr : prev, 0);
  }

  private formatTime(date: Date): string {
    const h = String(date.getHours()).padStart(2, '0');
    const m = String(date.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }

  private clamp(val: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, val || 0));
  }

  updateTime(field: 'startH' | 'startM' | 'endH' | 'endM', value: number) {
    const v = Number(value);
    if (field === 'startH') this.editStartHour = this.clamp(v, 0, 23);
    else if (field === 'startM') this.editStartMinute = this.clamp(v, 0, 59);
    else if (field === 'endH') this.editEndHour = this.clamp(v, 0, 23);
    else this.editEndMinute = this.clamp(v, 0, 59);

    this.editedStartDate = new Date(this.editedStartDate);
    this.editedStartDate.setHours(this.editStartHour, this.editStartMinute, 0, 0);
    this.editedEndDate = new Date(this.editedEndDate);
    this.editedEndDate.setHours(this.editEndHour, this.editEndMinute, 0, 0);
    this.durationText = this.calculateDurationTextFrom(this.editedStartDate, this.editedEndDate);
    this.cdr.markForCheck();
  }

  private calculateDurationText(): string {
    return this.calculateDurationTextFrom(this.startTime, this.endTime);
  }

  private calculateDurationTextFrom(start: Date, end: Date): string {
    if (!start || !end) return '';
    const diff = end.getTime() - start.getTime();
    if (diff <= 0) return '0m';
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    if (hours < 1) return `${minutes}m`;
    return `${hours}h ${minutes}m`;
  }

  // ── Phase picker (searchable) ────────────────────────────────────────────

  get selectedStageLabel(): string {
    const stage = this.filteredStages.find(s => s.projectStageId === this.selectedStageId);
    return stage ? `${stage.stageName} (${this.getStatusText(stage.status)})` : '';
  }

  toggleStageDropdown() {
    this.stageDropdownOpen = !this.stageDropdownOpen;
    if (this.stageDropdownOpen) {
      this.stageSearch = '';
      this.filterStageSearch();
      setTimeout(() => this.stageSearchInput?.nativeElement.focus());
    }
  }

  onStageDropdownFocusOut(event: FocusEvent) {
    const host = event.currentTarget as HTMLElement;
    const next = event.relatedTarget as Node | null;
    if (!next || !host.contains(next)) {
      this.stageDropdownOpen = false;
      this.cdr.markForCheck();
    }
  }

  filterStageSearch() {
    const q = this.stageSearch.trim().toLowerCase();
    this.searchedStages = q
      ? this.filteredStages.filter(st => (st.stageName || '').toLowerCase().includes(q))
      : [...this.filteredStages];
  }

  selectStage(stage: ProjectStage) {
    this.selectedStageId = stage.projectStageId ?? '';
    this.stageDropdownOpen = false;
    this.stageSearch = '';
    this.onStageChange();
    this.cdr.markForCheck();
  }

  // ── Task picker (searchable) ─────────────────────────────────────────────

  get selectedTaskLabel(): string {
    const task = this.stageTasks.find(t => t.id === this.selectedTaskId);
    if (!task) return '';
    return task.status ? `${task.nameTask} (${this.getStatusText(task.status)})` : task.nameTask;
  }

  toggleTaskDropdown() {
    this.taskDropdownOpen = !this.taskDropdownOpen;
    if (this.taskDropdownOpen) {
      this.taskSearch = '';
      this.creatingTask = false;
      this.newTaskName = '';
      this.filterTaskSearch();
      setTimeout(() => this.taskSearchInput?.nativeElement.focus());
    }
  }

  onTaskDropdownFocusOut(event: FocusEvent) {
    const host = event.currentTarget as HTMLElement;
    const next = event.relatedTarget as Node | null;
    if (!next || !host.contains(next)) {
      this.taskDropdownOpen = false;
      this.creatingTask = false;
      this.cdr.markForCheck();
    }
  }

  filterTaskSearch() {
    const q = this.taskSearch.trim().toLowerCase();
    this.searchedTasks = q
      ? this.filteredTasks.filter(t => (t.nameTask || '').toLowerCase().includes(q))
      : [...this.filteredTasks];
  }

  selectTask(task: Task) {
    this.selectedTaskId = task.id ?? '';
    this.taskDropdownOpen = false;
    this.taskSearch = '';
    this.cdr.markForCheck();
  }

  // ── Quick task creation ──────────────────────────────────────────────────

  /** Opens the inline create field, pre-filled with whatever was searched for. */
  startCreatingTask() {
    this.creatingTask = true;
    this.newTaskName = this.taskSearch.trim();
    this.cdr.markForCheck();
    setTimeout(() => this.newTaskInput?.nativeElement.focus());
  }

  cancelCreatingTask() {
    this.creatingTask = false;
    this.newTaskName = '';
    this.cdr.markForCheck();
  }

  /**
   * Creates a task in the selected phase and selects it, so a phase with no task
   * stops being a dead end in this modal. The due date defaults to the phase end
   * so the task is not left dateless.
   */
  async createQuickTask() {
    const name = this.newTaskName.trim();
    if (!name || !this.selectedStageId || this.isSavingTask) return;

    this.isSavingTask = true;
    try {
      const stage = this.filteredStages.find(st => st.projectStageId === this.selectedStageId);
      const created = await this.taskService.createTask({
        nameTask: name,
        projectStageId: this.selectedStageId,
        status: 'NOTBEGIN',
        priority: 'MEDIUM',
        limitDateToDo: stage?.endDate ? new Date(stage.endDate + 'T12:00:00').toISOString() : null,
      } as Task);

      if (created?.id) {
        // observeQuery will refresh allTasks, but select it now so the user can save.
        const task: Task = { id: created.id, nameTask: name, projectStageId: this.selectedStageId, status: 'NOTBEGIN' };
        this.stageTasks = [...this.stageTasks, task];
        this.applyTaskFilter();
        this.selectedTaskId = created.id;
      }
      this.creatingTask = false;
      this.newTaskName = '';
      this.taskDropdownOpen = false;
    } catch (e) {
      console.error('[EntryModal] Failed to create task', e);
    } finally {
      this.isSavingTask = false;
      this.cdr.markForCheck();
    }
  }

  // ── Project picker (searchable, alphabetical) ────────────────────────────

  /** Label shown on the picker trigger. */
  get selectedProjectLabel(): string {
    const proj = this.projects.find(p => p.projectId === this.selectedProjectId);
    if (!proj) return '';
    return `${proj.projectName} (${proj.tenant?.name || 'No tenant'})`;
  }

  toggleProjectDropdown() {
    this.projectDropdownOpen = !this.projectDropdownOpen;
    if (this.projectDropdownOpen) {
      this.projectSearch = '';
      this.filterProjects();
      // Focus the search field once the panel is rendered
      setTimeout(() => this.projectSearchInput?.nativeElement.focus());
    }
  }

  /** Closes the dropdown when focus leaves the picker entirely. */
  onProjectDropdownFocusOut(event: FocusEvent) {
    const host = event.currentTarget as HTMLElement;
    const next = event.relatedTarget as Node | null;
    if (!next || !host.contains(next)) {
      this.projectDropdownOpen = false;
      this.cdr.markForCheck();
    }
  }

  filterProjects() {
    const q = this.projectSearch.trim().toLowerCase();
    this.filteredProjects = q
      ? this.projects.filter(p =>
          (p.projectName || '').toLowerCase().includes(q) ||
          (p.tenant?.name || '').toLowerCase().includes(q))
      : [...this.projects];
  }

  selectProject(project: Project) {
    this.selectedProjectId = project.projectId ?? '';
    this.projectDropdownOpen = false;
    this.projectSearch = '';
    this.filterProjects();
    this.onProjectChange();
    this.cdr.markForCheck();
  }

  onProjectChange() {
    const proj = this.projects.find(p => p.projectId === this.selectedProjectId);
    this.projectName = proj ? proj.projectName : '';
    if (this.selectedProjectId) this.filteredStages = this.allStages.filter(s => s.projectId === this.selectedProjectId);
    else this.filteredStages = [];
    this.selectedStageId = '';
    this.stageTasks = [];
    this.filteredTasks = [];
    this.selectedTaskId = '';
    // Keep both search lists in step with the new project.
    this.stageSearch = '';
    this.taskSearch = '';
    this.filterStageSearch();
    this.filterTaskSearch();
  }

  /** Status priority: INPROGRESS first, NOTBEGIN second, DONE last */
  private statusOrder(status: string | null | undefined): number {
    switch (status) {
      case 'INPROGRESS': return 0;
      case 'NOTBEGIN': return 1;
      case 'DONE': return 2;
      default: return 1;
    }
  }

  onStageChange() {
    if (this.selectedStageId) {
      this.stageTasks = this.allTasks
        .filter(t => t.projectStageId === this.selectedStageId)
        .sort((a, b) => this.statusOrder(a.status) - this.statusOrder(b.status));
    } else {
      this.stageTasks = [];
    }
    this.taskStatusFilter = 'open';
    this.selectedTaskId = '';
    this.applyTaskFilter();
  }

  setTaskStatusFilter(filter: 'open' | 'all') {
    this.taskStatusFilter = filter;
    this.applyTaskFilter();
    this.cdr.markForCheck();
  }

  /** Keeps `filteredTasks` in sync with the open/all switch. */
  private applyTaskFilter() {
    this.filteredTasks = this.taskStatusFilter === 'all'
      ? [...this.stageTasks]
      : this.stageTasks.filter(t => t.status !== 'DONE');
    // Drop a selection that the filter just hid
    if (this.selectedTaskId && !this.filteredTasks.some(t => t.id === this.selectedTaskId)) {
      this.selectedTaskId = '';
    }
    this.filterTaskSearch();
  }

  /** Number of done tasks hidden by the 'open' filter (used for the switch hint). */
  get doneTaskCount(): number {
    return this.stageTasks.filter(t => t.status === 'DONE').length;
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.selectedFiles = [...this.selectedFiles, ...Array.from(input.files)];
    }
  }

  async onSave() {
    this.isUploading = true;
    try {
      let imagePaths: string[] = [];
      if (this.selectedFiles.length > 0) {
        const tempId = crypto.randomUUID();
        imagePaths = await this.fileService.uploadFiles(this.selectedFiles, 'timeEntryImage', tempId);
      }

      let finalName: string;
      if (this.entrySource === 'crm') {
        const task = this.crmTasks.find(t => t.id === this.selectedCrmTaskId);
        finalName = task ? `[Task] ${task.name}` : '[Task]';
      } else {
        finalName = this.projectName;
        const selectedStage = this.allStages.find(s => s.projectStageId === this.selectedStageId);
        if (selectedStage) finalName += ` - ${selectedStage.stageName}`;
        const selectedTask = this.allTasks.find(t => t.id === this.selectedTaskId)
          ?? this.stageTasks.find(t => t.id === this.selectedTaskId);
        if (selectedTask) finalName += ` - ${selectedTask.nameTask}`;
      }

      this.save.emit({
        content: this.content,
        imagePaths,
        projectName: finalName,
        projectId: this.entrySource === 'project' ? (this.selectedProjectId || null) : null,
        stageId: this.entrySource === 'project' ? (this.selectedStageId || null) : null,
        startTime: this.editedStartDate,
        endTime: this.editedEndDate,
        billable: this.billable,
      });
    } catch (err) {
      console.error('Save failed', err);
    } finally {
      this.isUploading = false;
    }
  }

  filterTask() {
    console.log('🟢 [MODAL] filterTask called with', this.allTimelineActivity.length, 'total activities');
    console.log('🟢 [MODAL] Filtering between', this.startTime.toISOString(), 'and', this.endTime.toISOString());

    const filteredTasks = this.allTimelineActivity.filter(activity => {
      if (!activity || !activity.name || !activity.title) return false;
      const taskTime = new Date(activity.startTime);
      const matches = taskTime >= this.startTime && taskTime <= this.endTime;
      if (this.allTimelineActivity.length <= 5) {
        console.log('🔍 [MODAL] Activity:', activity.title, 'startTime:', new Date(activity.startTime).toISOString(), 'matches:', matches);
      }
      return matches;
    });

    console.log('🟢 [MODAL] After filter:', filteredTasks.length, 'activities match period');

    const groupedTasksMap = new Map();
    filteredTasks.forEach(activity => {
      const key = `${activity.name.toLowerCase()}|${activity.title.toLowerCase()}`;
      if (!groupedTasksMap.has(key)) {
        groupedTasksMap.set(key, { ...activity });
      } else {
        const existing = groupedTasksMap.get(key);
        existing.duration += activity.duration;
        existing.startTime = new Date(Math.min(new Date(existing.startTime).getTime(), new Date(activity.startTime).getTime()));
        existing.endTime = new Date(Math.max(new Date(existing.endTime).getTime(), new Date(activity.endTime).getTime()));
      }
    });

    // Ne pas écraser allTimelineActivity, créer une nouvelle variable pour les activités filtrées
    const groupedActivities = Array.from(groupedTasksMap.values());
    console.log('✅ [MODAL] After grouping:', groupedActivities.length, 'unique activities');

    // Stocker les activités filtrées dans allTimelineActivity pour l'affichage
    this.allTimelineActivity = groupedActivities;
  }

  // Set pour des lookups O(1)
  selectedActivityIds = new Set<number>();

  addSelectedActivity(task: TimelineTask) {
    if (this.selectedActivityIds.has(task.id)) {
      this.selectedActivityIds.delete(task.id);
      this.selectedActivity = this.selectedActivity.filter(t => t.id !== task.id);
    } else {
      this.selectedActivityIds.add(task.id);
      this.selectedActivity.push(task);
    }
    this.cdr.markForCheck();
  }

  isSelected(activity: TimelineTask): boolean {
    return this.selectedActivityIds.has(activity.id);
  }

  async askAi() {
    if (this.posthogService.featureFlagAI()) {
      const system = `You are a professional assistant specializing in writing invoice descriptions.
      Your responses must be clear, concise, and suitable for accounting purposes.
      Summarize all the tasks done in 1-3 sentences.
      Avoid unnecessary details and don't do anything else.
      `;
      const tasksList = this.selectedActivity
        .map(task => `- ${task.title}`)
        .join('\n');
      const question = `User preferences:${this.userPrompt}Project: ${this.projectName}Tasks:${tasksList}`.trim();
      this.content = await this.aiService.askMistral(
        question,
        system
      );
      this.cdr.markForCheck();
    } else {
      console.error("Feature flags to ask AI is disabled")
    }
  }

  getStatusText(value: "NOTBEGIN" | "INPROGRESS" | "DONE" | null): string {
    const status = this.statusOptions.find(s => s.value === value);
    return status ? status.text : '';
  }

  // Validation: all fields must be filled
  isFormValid(): boolean {
    if (this.entrySource === 'crm') {
      return !!(this.selectedCrmTaskId && this.content.trim());
    }
    return !!(
      this.selectedProjectId &&
      this.selectedStageId &&
      this.selectedTaskId &&
      this.content.trim()
    );
  }
}
